import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  styled,
  alpha,
  useTheme,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  Collapse,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Card,
  CardContent,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import BadgeIcon from "@mui/icons-material/Badge";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import TimerIcon from "@mui/icons-material/Timer";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import UpdateIcon from "@mui/icons-material/Update";
import NotificationSnackbar from "./NotificationBar";

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&::-webkit-scrollbar": { height: "8px", width: "8px" },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.4) },
  },
}));

function WrittenExamDetails() {
  const theme = useTheme();
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";
  const DEFAULT_COLOR = "#FFFFFF";
  const [searchTerm, setSearchTerm] = useState("");
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showData, setShowData] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const actionMenuOpen = Boolean(anchorEl);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [examToView, setExamToView] = useState(null);
  const [editAssessmentOpen, setEditAssessmentOpen] = useState(false);
  const [updateTimeOpen, setUpdateTimeOpen] = useState(false);
  const [addMinutesOpen, setAddMinutesOpen] = useState(false);
  const [assessmentForm, setAssessmentForm] = useState({
    externalAssmentTitle: "",
    externalAssmentUrl: "",
    externalAssmentScored: "",
  });
  const [timeForm, setTimeForm] = useState({
    seatingDate: "",
    startTime: "",
    endTime: "",
  });
  const [minutesToAdd, setMinutesToAdd] = useState(15);
  const [additionalMinutesComments, setAdditionalMinutesComments] = useState("");

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleSearch = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setShowData(false);
      setExams([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await fetch("/api/support/written-examin-details", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ advertisementId: term }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = (data && data.message) || `HTTP ${res.status}`;
        setError(msg);
        setShowData(true);
        setExams([]);
        return;
      }
      const list = Array.isArray(data) ? data : data ? [data] : [];
      if (list.length > 0) {
        setExams(list);
        setShowData(true);
        setError(null);
      } else {
        setExams([]);
        setShowData(true);
        setError("No written exam records found for this Advertisement ID.");
      }
    } catch (err) {
      console.error("Search failed:", err);
      setError("Search failed. Please try again.");
      setShowData(true);
      setExams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setError(null);
    setShowData(false);
    setExams([]);
  };

  const handleMenuOpen = (event, exam) => {
    setAnchorEl(event.currentTarget);
    setSelectedExam(exam);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const updateExamInState = (updatedItem) => {
    setExams((prev) =>
      prev.map((e) => (e.examId === updatedItem.examId ? { ...e, ...updatedItem } : e))
    );
    if (examToView && examToView.examId === updatedItem.examId) {
      setExamToView((prev) => ({ ...prev, ...updatedItem }));
    }
  };

  const handleActivateExam = async (examItem) => {
    const item = examItem || selectedExam;
    if (!item) return;
    try {
      setActionLoading(true);
      const res = await fetch("/api/support/activate-written-examin", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          examId: item.examId,
          advertisementId: item.advertisementId,
        }),
      });
      const result = await res.json().catch(() => ({}));
      if (res.ok) {
        setSnackbarMessage(
          result.message || "Written exam activated successfully"
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        updateExamInState({
          examId: item.examId,
          isActive: true,
          wasExamMarkingPublished: false,
          publishedOn: null,
          publishedBy: null,
        });
      } else {
        throw new Error(result.message || "Failed to activate written exam.");
      }
    } catch (err) {
      setSnackbarMessage(err.message || "Error activating written exam.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setActionLoading(false);
      handleMenuClose();
    }
  };

  const handleOpenEditAssessment = (examItem) => {
    const item = examItem || selectedExam;
    if (!item) return;
    setAssessmentForm({
      externalAssmentTitle: item.externalAssmentTitle || "",
      externalAssmentUrl: item.externalAssmentUrl || "",
      externalAssmentScored:
        item.externalAssmentScored !== null && item.externalAssmentScored !== undefined
          ? String(item.externalAssmentScored)
          : "",
    });
    setSelectedExam(item);
    setEditAssessmentOpen(true);
    handleMenuClose();
  };

  const handleSaveAssessment = async () => {
    if (!selectedExam) return;
    let scoredNumber = undefined;
    if (
      assessmentForm.externalAssmentScored !== "" &&
      assessmentForm.externalAssmentScored !== null &&
      assessmentForm.externalAssmentScored !== undefined
    ) {
      const parsed = Number(assessmentForm.externalAssmentScored);
      if (isNaN(parsed) || parsed < 0) {
        setSnackbarMessage("External assessment score must be a valid positive number.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }
      scoredNumber = parsed;
    }
    try {
      setActionLoading(true);
      const payload = {
        examId: selectedExam.examId,
        externalAssmentUrl: assessmentForm.externalAssmentUrl,
        externalAssmentTitle: assessmentForm.externalAssmentTitle,
      };
      if (scoredNumber !== undefined) {
        payload.externalAssmentScored = scoredNumber;
      }
      const res = await fetch("/api/support/update-external-assessment-details", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json().catch(() => ({}));
      if (res.ok) {
        setSnackbarMessage(result.message || "External assessment details updated successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        updateExamInState({
          examId: selectedExam.examId,
          externalAssmentTitle: assessmentForm.externalAssmentTitle,
          externalAssmentUrl: assessmentForm.externalAssmentUrl,
          externalAssmentScored: scoredNumber,
        });
        setEditAssessmentOpen(false);
      } else {
        throw new Error(result.message || "Failed to update assessment details.");
      }
    } catch (err) {
      setSnackbarMessage(err.message || "Error saving assessment details.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenUpdateTime = (examItem) => {
    const item = examItem || selectedExam;
    if (!item) return;
    setTimeForm({
      seatingDate: item.seatingDate ? item.seatingDate.split("T")[0] : "",
      startTime: item.startTime || "",
      endTime: item.endTime || "",
    });
    setSelectedExam(item);
    setUpdateTimeOpen(true);
    handleMenuClose();
  };

  const handleSaveTime = async () => {
    if (!selectedExam) return;
    if (!timeForm.seatingDate || !timeForm.startTime || !timeForm.endTime) {
      setSnackbarMessage("Date, Start Time, and End Time are required.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    if (timeForm.startTime >= timeForm.endTime) {
      setSnackbarMessage("End time must be greater than start time.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    try {
      setActionLoading(true);
      const res = await fetch("/api/support/update-written-exam-schedule", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          examId: selectedExam.examId,
          seatingDate: timeForm.seatingDate,
          startTime: timeForm.startTime,
          endTime: timeForm.endTime,
        }),
      });
      const result = await res.json().catch(() => ({}));
      if (res.ok) {
        setSnackbarMessage(result.message || "Written exam schedule updated successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        updateExamInState({ examId: selectedExam.examId, ...timeForm });
        setUpdateTimeOpen(false);
      } else {
        throw new Error(result.message || "Failed to update exam schedule.");
      }
    } catch (err) {
      setSnackbarMessage(err.message || "Error updating exam schedule.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenAddMinutes = (examItem) => {
    const item = examItem || selectedExam;
    if (!item) return;
    setMinutesToAdd(15);
    setAdditionalMinutesComments("");
    setSelectedExam(item);
    setAddMinutesOpen(true);
    handleMenuClose();
  };

  const handleSaveMinutes = async () => {
    if (!selectedExam) return;
    const extra = Number(minutesToAdd);
    if (!Number.isInteger(extra) || extra <= 0) {
      setSnackbarMessage("Additional minutes must be a positive integer.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    try {
      setActionLoading(true);
      const res = await fetch("/api/support/add-additional-exam-minutes", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          examId: selectedExam.examId,
          additionalMinutes: extra,
          additionalMinutesComments: additionalMinutesComments || null,
        }),
      });
      const result = await res.json().catch(() => ({}));
      if (res.ok) {
        setSnackbarMessage(
          result.message || "Additional examination minutes added successfully"
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        const newTotalAdditionalMinutes =
          result?.data?.totalAdditionalMinutes ??
          (Number(selectedExam.additionalMinutes || 0) + extra);
        const newTotalDuration = (selectedExam.durationMin || 0) + extra;
        updateExamInState({
          examId: selectedExam.examId,
          durationMin: newTotalDuration,
          additionalMinutes: newTotalAdditionalMinutes,
          additionalMinutesComments: additionalMinutesComments || selectedExam.additionalMinutesComments,
        });
        setAddMinutesOpen(false);
      } else {
        throw new Error(result.message || "Failed to add additional minutes.");
      }
    } catch (err) {
      setSnackbarMessage(err.message || "Error adding minutes.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const DetailField = ({ label, value, icon: Icon, isLink = false }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
      {Icon && (
        <Box
          sx={{
            mr: 2,
            p: 1.2,
            borderRadius: 2,
            backgroundColor: alpha(SECONDARY_COLOR, 0.08),
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Icon fontSize="small" sx={{ color: SECONDARY_COLOR }} />
        </Box>
      )}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            display: "block",
            mb: 0.3,
          }}
        >
          {label}
        </Typography>
        {isLink && value && value !== "N/A" ? (
          <Box
            component="a"
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontWeight: 600,
              color: SECONDARY_COLOR,
              wordWrap: "break-word",
              fontSize: "0.95rem",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {value} <OpenInNewIcon sx={{ fontSize: "0.9rem" }} />
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: PRIMARY_COLOR,
              wordWrap: "break-word",
              fontSize: "0.95rem",
            }}
          >
            {value !== undefined && value !== null && value !== "" ? value : "N/A"}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        minHeight: "75vh",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          mb: 4,
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY_COLOR }}>
          Written Exam Configuration & Support Management
        </Typography>
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border: `1px solid ${alpha(PRIMARY_COLOR, 0.1)}`,
          bgcolor: alpha(PRIMARY_COLOR, 0.015),
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 1, color: PRIMARY_COLOR, fontWeight: 600 }}
        >
          Search Written Exam Details
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Enter an Advertisement ID to adjust exam time, activate sessions, add extra minutes, or edit external assessment integrations.
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 3,
          }}
        >
          <TextField
            placeholder="Enter Advertisement ID (e.g. 2605000501)"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              const v = e.target.value;
              setSearchTerm(v);
              if (!v.trim()) setShowData(false);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm && (
                    <IconButton
                      aria-label="clear"
                      size="small"
                      onClick={handleClearSearch}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              maxWidth: 500,
              bgcolor: "white",
              borderRadius: 1,
            }}
          />
          <Button
            variant="contained"
            disableElevation
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SearchIcon />
              )
            }
            onClick={handleSearch}
            disabled={loading}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              bgcolor: PRIMARY_COLOR,
              "&:hover": { bgcolor: SECONDARY_COLOR },
            }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </Box>
        <Collapse in={showData} timeout="auto">
          {error ? (
            <Typography
              color="error"
              sx={{ fontSize: "0.95rem", fontWeight: 500 }}
            >
              {error}
            </Typography>
          ) : exams && exams.length > 0 ? (
            <Box>
              <Typography
                sx={{ fontWeight: 600, color: PRIMARY_COLOR, mb: 1.5 }}
              >
                Found {exams.length} Written Exam
                {exams.length > 1 ? "s" : ""}
              </Typography>
              <ResponsiveTableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Exam ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Position
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Seating Date
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Time Window
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Duration
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Exam Active?
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Marking Published
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exams.map((exam, index) => (
                      <TableRow key={exam.examId || index} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {exam.examId ?? "-"}
                        </TableCell>
                        <TableCell>{exam.position ?? "-"}</TableCell>
                        <TableCell>{formatDate(exam.seatingDate)}</TableCell>
                        <TableCell align="center">
                          {exam.startTime && exam.endTime
                            ? `${exam.startTime} - ${exam.endTime}`
                            : "-"}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<TimerIcon fontSize="small" />}
                            label={exam.durationMin ? `${exam.durationMin} mins` : "-"}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={exam.isActive ? "Active" : "Inactive"}
                            size="small"
                            color={exam.isActive ? "success" : "default"}
                            sx={{ fontWeight: 600, borderRadius: 2 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={exam.wasExamMarkingPublished ? "Published" : "Unpublished"}
                            size="small"
                            color={exam.wasExamMarkingPublished ? "info" : "warning"}
                            variant={exam.wasExamMarkingPublished ? "filled" : "outlined"}
                            sx={{ fontWeight: 600, borderRadius: 2 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, exam)}
                            size="small"
                            sx={{
                              color: PRIMARY_COLOR,
                              backgroundColor: alpha(PRIMARY_COLOR, 0.05),
                              "&:hover": {
                                backgroundColor: alpha(PRIMARY_COLOR, 0.15),
                              },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTableContainer>
              <Menu
                anchorEl={anchorEl}
                open={actionMenuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
                    mt: 1,
                    minWidth: 230,
                    borderRadius: 2,
                    "& .MuiMenuItem-root": {
                      px: 2,
                      py: 1.2,
                      "&:hover": {
                        backgroundColor: alpha(PRIMARY_COLOR, 0.04),
                      },
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem
                  onClick={() => {
                    setExamToView(selectedExam);
                    setViewModalOpen(true);
                    handleMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <VisibilityIcon fontSize="small" sx={{ color: SECONDARY_COLOR }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="View Details"
                    primaryTypographyProps={{ fontWeight: 500, fontSize: "0.9rem" }}
                  />
                </MenuItem>
                <MenuItem onClick={() => handleActivateExam(selectedExam)}>
                  <ListItemIcon>
                    <PlayCircleOutlineIcon
                      fontSize="small"
                      color="success"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Activate Exam"
                    primaryTypographyProps={{ fontWeight: 500, fontSize: "0.9rem" }}
                  />
                </MenuItem>
                <MenuItem onClick={() => handleOpenEditAssessment(selectedExam)}>
                  <ListItemIcon>
                    <EditNoteIcon fontSize="small" sx={{ color: SECONDARY_COLOR }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Edit Assessment Info"
                    primaryTypographyProps={{ fontWeight: 500, fontSize: "0.9rem" }}
                  />
                </MenuItem>
                <MenuItem onClick={() => handleOpenUpdateTime(selectedExam)}>
                  <ListItemIcon>
                    <UpdateIcon fontSize="small" sx={{ color: SECONDARY_COLOR }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Update Exam Schedule"
                    primaryTypographyProps={{ fontWeight: 500, fontSize: "0.9rem" }}
                  />
                </MenuItem>
                <MenuItem onClick={() => handleOpenAddMinutes(selectedExam)}>
                  <ListItemIcon>
                    <MoreTimeIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Add Extra Minutes"
                    primaryTypographyProps={{ fontWeight: 500, fontSize: "0.9rem" }}
                  />
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>
              No results found.
            </Typography>
          )}
        </Collapse>
      </Paper>
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": { borderRadius: "16px", backgroundColor: "#f8fafc" },
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: SECONDARY_COLOR,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: DEFAULT_COLOR }}>
            Written Exam Session Details
          </Typography>
          <IconButton
            onClick={() => setViewModalOpen(false)}
            size="small"
            sx={{ color: DEFAULT_COLOR }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
          {examToView && (
            <Box>
              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  p: 3,
                  mb: 4,
                  borderRadius: 4,
                  border: `1px solid ${alpha(SECONDARY_COLOR, 0.15)}`,
                  background: `linear-gradient(135deg, ${DEFAULT_COLOR} 0%, ${alpha(SECONDARY_COLOR, 0.05)} 100%)`,
                }}
              >
                <Avatar
                  sx={{
                    width: 86,
                    height: 86,
                    bgcolor: PRIMARY_COLOR,
                    fontSize: "2rem",
                    fontWeight: 700,
                    mb: { xs: 2, sm: 0 },
                    mr: { sm: 3 },
                    boxShadow: `0 4px 14px ${alpha(PRIMARY_COLOR, 0.3)}`,
                  }}
                >
                  <AssignmentIcon sx={{ fontSize: "2.5rem" }} />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: PRIMARY_COLOR, mb: 0.5 }}
                  >
                    {examToView.position || `Exam #${examToView.examId}`}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: SECONDARY_COLOR, fontWeight: 600, mb: 1.5 }}
                  >
                    Advertisement ID: {examToView.advertisementId} • Exam ID: {examToView.examId}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                    <Chip
                      label={examToView.isActive ? "Exam Active" : "Exam Inactive"}
                      color={examToView.isActive ? "success" : "default"}
                      size="small"
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                    />
                    <Chip
                      label={
                        examToView.wasExamMarkingPublished
                          ? "Marking Published"
                          : "Marking Pending"
                      }
                      color={examToView.wasExamMarkingPublished ? "info" : "warning"}
                      size="small"
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PlayCircleOutlineIcon />}
                      onClick={() => handleActivateExam(examToView)}
                      disabled={actionLoading}
                      sx={{
                        bgcolor: "success.main",
                        "&:hover": {
                          bgcolor: "success.dark",
                        },
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 2,
                      }}
                    >
                      Activate Exam
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<MoreTimeIcon />}
                      onClick={() => handleOpenAddMinutes(examToView)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 2,
                        color: PRIMARY_COLOR,
                        borderColor: alpha(PRIMARY_COLOR, 0.3),
                      }}
                    >
                      Add Minutes
                    </Button>
                  </Box>
                </Box>
              </Paper>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: `1px solid ${alpha(PRIMARY_COLOR, 0.08)}`,
                      height: "100%",
                      boxShadow: "0px 4px 20px rgba(0,0,0,0.02)",
                    }}
                  >
                    <CardContent sx={{ p: 3, pt: 4 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <AccessTimeIcon sx={{ color: SECONDARY_COLOR, mr: 1 }} />
                          <Typography variant="h6" sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}>
                            Schedule & Timing
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<UpdateIcon />}
                          onClick={() => handleOpenUpdateTime(examToView)}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          Edit Schedule
                        </Button>
                      </Box>
                      <Divider sx={{ mb: 3, borderColor: alpha(PRIMARY_COLOR, 0.05) }} />
                      <DetailField
                        icon={CalendarMonthIcon}
                        label="Seating Date"
                        value={formatDate(examToView.seatingDate)}
                      />
                      <DetailField
                        icon={AccessTimeIcon}
                        label="Exam Window"
                        value={
                          examToView.startTime && examToView.endTime
                            ? `${examToView.startTime} - ${examToView.endTime}`
                            : "N/A"
                        }
                      />
                      <DetailField
                        icon={TimerIcon}
                        label="Duration"
                        value={examToView.durationMin ? `${examToView.durationMin} Minutes` : "N/A"}
                      />
                      <DetailField
                        icon={CalendarMonthIcon}
                        label="Marking Published On"
                        value={formatDateTime(examToView.publishedOn)}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: `1px solid ${alpha(PRIMARY_COLOR, 0.08)}`,
                      height: "100%",
                      boxShadow: "0px 4px 20px rgba(0,0,0,0.02)",
                    }}
                  >
                    <CardContent sx={{ p: 3, pt: 4 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <WorkOutlineIcon sx={{ color: SECONDARY_COLOR, mr: 1 }} />
                          <Typography variant="h6" sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}>
                            Advertisement & External Assessment
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<EditNoteIcon />}
                          onClick={() => handleOpenEditAssessment(examToView)}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          Edit Info
                        </Button>
                      </Box>
                      <Divider sx={{ mb: 3, borderColor: alpha(PRIMARY_COLOR, 0.05) }} />
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <DetailField
                            icon={BadgeIcon}
                            label="Exam ID"
                            value={examToView.examId}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DetailField
                            icon={BadgeIcon}
                            label="Advertisement ID"
                            value={examToView.advertisementId}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <DetailField
                            icon={WorkOutlineIcon}
                            label="Position"
                            value={examToView.position}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="Advertisement Status"
                            value={examToView.advertismentStatuses}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <DetailField
                            icon={AssignmentIcon}
                            label="External Assessment Title"
                            value={examToView.externalAssmentTitle || "No External Assessment Title"}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <DetailField
                            icon={LinkIcon}
                            label="External Assessment URL"
                            value={examToView.externalAssmentUrl || "N/A"}
                            isLink={Boolean(examToView.externalAssmentUrl)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="External Assessment Scored"
                            value={
                              examToView.externalAssmentScored !== null &&
                              examToView.externalAssmentScored !== undefined
                                ? String(examToView.externalAssmentScored)
                                : "N/A"
                            }
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3, justifyContent: "space-between", backgroundColor: "#ffffff" }}>
          <Button
            onClick={() => setViewModalOpen(false)}
            color="inherit"
            sx={{ fontWeight: 600 }}
          >
            Close
          </Button>
          <Button
            onClick={() => setViewModalOpen(false)}
            variant="contained"
            disableElevation
            sx={{
              fontWeight: 600,
              px: 3,
              backgroundColor: SECONDARY_COLOR,
              "&:hover": { backgroundColor: PRIMARY_COLOR },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editAssessmentOpen}
        onClose={() => setEditAssessmentOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": { borderRadius: "16px" },
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: SECONDARY_COLOR,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: DEFAULT_COLOR }}>
            Edit External Assessment Details
          </Typography>
          <IconButton
            onClick={() => setEditAssessmentOpen(false)}
            size="small"
            sx={{ color: DEFAULT_COLOR }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedExam && (
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Editing assessment info for <strong>{selectedExam.position}</strong> (Exam ID: {selectedExam.examId})
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="External Assessment Title"
                  placeholder="e.g. REB English Test Assessment"
                  value={assessmentForm.externalAssmentTitle}
                  onChange={(e) =>
                    setAssessmentForm((prev) => ({
                      ...prev,
                      externalAssmentTitle: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="External Assessment URL"
                  placeholder="https://..."
                  value={assessmentForm.externalAssmentUrl}
                  onChange={(e) =>
                    setAssessmentForm((prev) => ({
                      ...prev,
                      externalAssmentUrl: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: "any" }}
                  label="External Assessment Scored"
                  placeholder="e.g. 70"
                  value={assessmentForm.externalAssmentScored}
                  onChange={(e) =>
                    setAssessmentForm((prev) => ({
                      ...prev,
                      externalAssmentScored: e.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3, justifyContent: "space-between" }}>
          <Button
            onClick={() => setEditAssessmentOpen(false)}
            color="inherit"
            disabled={actionLoading}
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: SECONDARY_COLOR, px: 3 }}
            onClick={handleSaveAssessment}
            disabled={actionLoading}
          >
            {actionLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={updateTimeOpen}
        onClose={() => setUpdateTimeOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": { borderRadius: "16px" },
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: SECONDARY_COLOR,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: DEFAULT_COLOR }}>
            Update Exam Schedule
          </Typography>
          <IconButton
            onClick={() => setUpdateTimeOpen(false)}
            size="small"
            sx={{ color: DEFAULT_COLOR }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedExam && (
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Adjust seating schedule and time limits for <strong>{selectedExam.position}</strong> ({selectedExam.examId})
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Seating Date"
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={timeForm.seatingDate}
                  onChange={(e) =>
                    setTimeForm((prev) => ({ ...prev, seatingDate: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Time"
                  placeholder="11:25"
                  fullWidth
                  size="small"
                  value={timeForm.startTime}
                  onChange={(e) =>
                    setTimeForm((prev) => ({ ...prev, startTime: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="End Time"
                  placeholder="14:00"
                  fullWidth
                  size="small"
                  value={timeForm.endTime}
                  onChange={(e) =>
                    setTimeForm((prev) => ({ ...prev, endTime: e.target.value }))
                  }
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3, justifyContent: "space-between" }}>
          <Button
            onClick={() => setUpdateTimeOpen(false)}
            color="inherit"
            disabled={actionLoading}
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: SECONDARY_COLOR, px: 3 }}
            onClick={handleSaveTime}
            disabled={actionLoading || !timeForm.seatingDate || !timeForm.startTime || !timeForm.endTime}
          >
            {actionLoading ? "Saving..." : "Save Schedule"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={addMinutesOpen}
        onClose={() => setAddMinutesOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": { borderRadius: "16px" },
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: SECONDARY_COLOR,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: DEFAULT_COLOR }}>
            Add Additional Minutes
          </Typography>
          <IconButton
            onClick={() => setAddMinutesOpen(false)}
            size="small"
            sx={{ color: DEFAULT_COLOR }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedExam && (
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Current Exam Duration:{" "}
                  <strong>{selectedExam.durationMin || 0} minutes</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  This will add additional minutes to the examination countdown timer.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Additional Minutes"
                  type="number"
                  fullWidth
                  size="small"
                  inputProps={{ min: 1, step: 1 }}
                  value={minutesToAdd}
                  onChange={(e) => setMinutesToAdd(e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">min</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Additional Minutes Comments"
                  placeholder="Provide reason or justification for adding extra minutes..."
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  value={additionalMinutesComments}
                  onChange={(e) => setAdditionalMinutesComments(e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3, justifyContent: "space-between" }}>
          <Button
            onClick={() => setAddMinutesOpen(false)}
            color="inherit"
            disabled={actionLoading}
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: SECONDARY_COLOR, px: 3 }}
            onClick={handleSaveMinutes}
            disabled={actionLoading || !minutesToAdd || Number(minutesToAdd) <= 0}
          >
            {actionLoading ? "Adding..." : "Add Minutes"}
          </Button>
        </DialogActions>
      </Dialog>
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default WrittenExamDetails;